<template>
<div class="card p-3">
	<div class="card-body">

		<div v-if="brokenJobs.length > 0" class="alert alert-danger">
			{{brokenJobs.length}} jobs could not be imported. <a href="#" v-on:click.prevent="downloadJobsWithErrors">Click here</a>
			to download a file containing the unimported jobs with more information.
		</div>

		<!--- Show a file template, then upload a file --->
		<div v-if="stage == 'upload-file'">

			<upload-csv
				:fields="fileFields"
				:submit-visible="true"
				:working="fetchingContacts"
				v-on:input="fileUploaded"
				v-on:submit="fetchAllContacts(true)">

				<template slot="notes">
					<p v-if="jobCategoriesList">
						<b>Category Name</b> is one of: {{jobCategoriesList}}
					</p>
					<p v-if="revenueCodesList">
						<b>Revenue Code Name</b> is one of: {{revenueCodesList}}
					</p>
				</template>
			</upload-csv>

		</div>


		<!--- Allow the user to fix any issues with the file --->
		<div v-else-if="stage == 'fix-file'">

			<h2 class="mb-4">Fix Missing Customers</h2>

			<table class="table">
				<tr>
					<th>Job #</th>
					<th>Job Name</th>
					<th>Customer</th>
				</tr>

				<tr v-for="job in fixableJobs" :key="job.id">
					<td>{{job.job_num}}</td>
					<td>{{job.job_name}}</td>
					<td>
						<field
							:params="{type:'native-select', options: contactOptions}"
							v-on:change="setCustomer(job, $event)">
						</field>
					</td>
				</tr>
			</table>

			<div class="text-right">
				<button v-on:click="saveAndFetchNext" class="btn btn-primary" :disabled="false">
					Next
					<i class="fa fa-arrow-right"></i>
				</button>
			</div>
		</div>

		<div v-else>
			<div class="text-center alert alert-success pt-4">
				<i class="fa fa-3x fa-check-circle"></i>

				<h2>{{ numImported }} jobs imported!</h2>
			</div>
		</div>

	</div>
</div>
</template>
<script type="text/javascript">
import Field from "@/components/Field"
import UploadCsv from "@/components/UploadCsv.vue";

export default {
	name: "JobsImport",
	props: ["isService"],
	components: {
		Field,
		UploadCsv
	},
	data() {
		return {

			stage: 'upload-file',  // 'upload-file', 'fix-file', 'finish-import'

			// Which fields are required in the file?
			baseFileFields: [
				{ name: 'Customer Email',         description: false,  key: false,          type: 'text',     required: true,   examples: ['carnold@gmail.com'] },   // mapped to contact email after
				{ name: 'Job Number',             description: false,  key: 'job_num',      type: 'text',     required: true,   examples: ['000-01'] },
				{ name: 'Job Name',               description: false,  key: 'job_name',     type: 'text',     required: true,   examples: ['Office'] },
				{ name: 'Category Name',          description: false,  key: false,          type: 'text',     required: true,   examples: ['General'] },   // mapped to name after
				{ name: 'Revenue Code',           description: false,  key: false,          type: 'text',     required: true,   examples: ['4200'] },   // mapped to code after
				{ name: 'Description',            description: false,  key: 'description',  type: 'text',     required: true,   examples: ['Default office job'] },
				{ name: 'Address',                description: false,  key: 'address',      type: 'text',     required: true,   examples: ['32 Glengary Street'] },
				{ name: 'Suite',                  description: false,  key: 'suite',        type: 'text',     required: true,   examples: [''] },
				{ name: 'City',                   description: false,  key: 'city',         type: 'text',     required: true,   examples: ['Caledonia'] },
				{ name: 'Province/State',         description: false,  key: 'province',     type: 'province', required: true,   examples: ['ON'] },
				{ name: 'Country',                description: false,  key: 'country',      type: 'country',  required: true,   examples: ['CA'] },
				{ name: 'Postal Code',            description: false,  key: 'postal_code',  type: 'text',     required: true,   examples: ['N3W5G7'] },
				{ name: 'Customer PO',            description: false,  key: 'customer_po',  type: 'text',                       examples: ['X-2336'] },
				{ name: 'Permit Needed?',         description: false,  key: 'permit_needed',type: 'bool',                       examples: ['FALSE'] },

				{ name: 'Inspector Name',              description: false,  key: 'inspector_name',             type: 'text',    examples: [''] },
				{ name: 'Inspector Number',            description: false,  key: 'inspector_num',              type: 'text',    examples: [''] },
				{ name: 'Inspector Email',             description: false,  key: 'inspector_email',            type: 'text',    examples: [''] },
				{ name: 'Permit Notification Number',  description: false,  key: 'permit_notification_number', type: 'text',    examples: [''] },

				{ name: 'Pays Mileage?',               description: false,  key: 'pays_mileage',               type: 'bool',    examples: ['FALSE'] },
				{ name: 'Pays Per Diem?',              description: false,  key: 'pays_perdiem',               type: 'bool',    examples: ['FALSE'] },
				{ name: 'Is Archived?',                description: false,  key: 'archived',                   type: 'bool',    examples: ['FALSE'] },
				{ name: 'Holdback Percentage',         description: false,  key: 'holdback_percentage',        type: 'number',  examples: [''] },
			],

			revenueCodes: [],       // fetched
			jobCategories: [],      // fetched

			fileHeaders: [],        // Header row in the file
			fileRows: [],           // Body rows in the file

			fetchingContacts: false,   // are we in the process of fetching the contacts?
			allContacts: [],           // fetched; on file confirmation; all company contacts

			allJobs: [],            // fetched; on SAVE and creation (if working from a previous session)
			fixableJobs: [],        // fetched; on SAVE and creation (if working from a previous session)
			brokenJobs: [],         // set; jobs that returned an error when SAVED

			numImported: 0,       // # of contacts imported.  Set after import

		}
	},
	computed: {

		contactOptions() {
			return [
				{text: '',  value: -1},    // allow for unselected option
				...this.optionify(this.allContacts.sort(), 'name')
			]
		},

		nameToIndex() {
			/* Map the name of the header (ex. "Pays Mileage?") to its column index in the CSV file */
			let hash = {}
			this.fileFields.forEach(field => {
				hash[field.name] = this.fileHeaders.findIndex(name => name == field.name)
			})
			return hash
		},

		indexToKey() {
			/* Map the column index in the CSV file to the proper key ("job_num") */
			/* This is to account for CSVs with columns out of order */
			return this.fileHeaders.map(name => {
				const field = this.fileFields.find(field => field.name == name)
				return field ? field.key : false
			})
		},

		jobCategoriesList() {
			return this.jobCategories.map(jc => "'"+jc.name+"'").join(', ')
		},

		revenueCodesList() {
			return this.revenueCodes.map(rc => "'"+rc.code+"'").join(', ')
		},

		endpoint() {
			return (this.isService ? "Service" : "Job")
		},

		fileFields() {

			let fields = this.cloneObj(this.baseFileFields)

			if (this.isService) {

				// Remove "Holdback Percentage"
				const holdbackIndex = fields.findIndex(field => field.key == 'holdback_percentage')
				fields.splice(holdbackIndex, 1)

				// Add some other fields
				fields = fields.concat([
					{ name: 'Is Emergency Service?',  description: false,   key: 'is_emergency',     type: 'bool',     examples: ['FALSE'] },
					{ name: 'Estimated Time',         description: false,   key: 'estimated_time',   type: 'number',   examples: ['8.00'] },
					{ name: 'Internal Notes',         description: false,   key: 'internal_notes',   type: 'text',     examples: [''] },
					{ name: 'Permit Cost',            description: false,   key: 'permit_cost',      type: 'number',   examples: ['89.27'] },
				])
			}

			return fields
		}
	},
	methods: {

		downloadJobsWithErrors() {
			let csvHeaders = this.fileFields.map(field => field.name)
			csvHeaders = ['Error(s)', ...csvHeaders]

			const newCSV = [csvHeaders, ...this.brokenJobs]
			this.exportToCSV('invalid_jobs.csv', newCSV)
		},

		setCustomer(job, customerId) {
			job.customer_id = Math.max(customerId, 0)     // if -1 (unselected), swap to 0
			job.is_draft = customerId ? 0 : 1
		},

		saveAndFetchNext() {
			/*
			Save all jobs to TradeTraks
			Note that in the previous stage, all invalid jobs are marked with is_draft=1 while all valid jobs are marked with is_draft=0
			This is called when the user first begins the process, and each time a batch of jobs is finished being fixed
			 */
			let vm = this

			// only save jobs that have a customer id set
			const jobs = this.cloneObj(this.allJobs).filter(job => {
				return !('id' in job) || ('customer_id' in job && job.customer_id > 0)
			})

			this.saveJobs(jobs).then(response => {
				vm.fetchJobDrafts()
			})
		},

		fetchJobDrafts() {
			/*
			Fetch stored job drafts from a previous session.
			If stored drafts exist, then make sure we are on the "Fix File" page.
			 */
			let vm = this

			let payload = {
				fields: ['job_name', 'job_num'],
				filters: [['is_draft', 1]],
				limit: 25
			}

			this.getJSON(this.endpoint+"/get", payload).then(response => {
				if (response.success) {

					if (response.data.length > 0) {

						response.data.forEach(job => {
							job.customer_id = (job.customer_id == '0') ? null : job.customer_id
						})

						vm.allJobs = response.data
						vm.fixableJobs = response.data
						vm.setStage('fix-file')

						if (!vm.allContacts.length)
							vm.fetchAllContacts(false)

					} else if (vm.allJobs.length > 0) {
						vm.setStage('confirm-import')
					}
				}
			})
		},

		setAndSaveJobs() {
			let vm = this

			this.allJobs = []
			this.fixableJobs = []

			// Hash contact email to ID for faster performance
			let contactHash = {}
			this.allContacts.forEach(contact => {
				// If there is a hash conflict, mark the contact as null so that it can be manually fixed later
				// Otherwise, map to the id as intended
				if (contact.email in contactHash)
					contactHash[contact.email] = null
				else
					contactHash[contact.email] = contact.id
			})

			// Set up for later
			let defaultCheckInTolerance = null

			try {
				const jobDefaults = JSON.parse(this.$store.company.CompanySetting.job_defaults_json)
				defaultCheckInTolerance = jobDefaults.check_in_tolerance
			} catch (e) {
				return
			}

			const dateLanded = this.moment().format("YYYY-MM-DD")

			// All job nums must be unique so check beforehand.  Use dict to keep track for faster (O(1) < O(n)) referencing
			let jobNums = {}

			this.fileRows.forEach((row, index) => {
				let job = {
					fileRowIndex: index,
					check_in_tolerance: defaultCheckInTolerance,
					date_landed: dateLanded,
					location_type: 'address'
				}

				if (this.isService) {
					job.status = 'Dispatched'
				}

				row.forEach((cell, index) => {
					if (cell !== "" && index in this.indexToKey && this.indexToKey[index])
						job[this.indexToKey[index]] = cell
				})

				if ('archived' in job && !job.archived)
					delete job.archived

				if ('job_num' in job) {
					if (job.job_num in jobNums) {
						const type = (this.isService) ? 'service' : 'project'
						this.brokenJobs.push(["This job # already exists for another " + type, ...row])
						return
					} else {
						jobNums[job.job_num] = null
					}
				}

				// Set revenue code
				const revenueCodeIndex = this.nameToIndex['Revenue Code']
				const revenueCodeRecord = this.revenueCodes.find(rc => rc.code == row[revenueCodeIndex])
				if (!revenueCodeRecord) {
					this.brokenJobs.push(['Invalid revenue code', ...row])
					return
				}

				job.revenue_code_id = (revenueCodeRecord) ? revenueCodeRecord.id : null

				// Set category
				const jobCategoryIndex = this.nameToIndex['Category Name']
				const jobCategoryRecord = this.jobCategories.find(jc => jc.name == row[jobCategoryIndex])
				if (!jobCategoryRecord) {
					this.brokenJobs.push(['Invalid category name', ...row])
					return
				}

				job.category_id = (jobCategoryRecord) ? jobCategoryRecord.id : null

				// Set contact
				const emailIndex = this.nameToIndex['Customer Email']
				const contactId = contactHash[row[emailIndex]]
				job.customer_id = contactId
				job.is_draft = Number(!contactId)      // ex. 293 -> 0,  or 0 -> 1

				if (!contactId)
					this.fixableJobs.push(job)
				this.allJobs.push(job)
			})

			if (this.fixableJobs.length) {                       // Some jobs are missing a customer, advance to the warning screen before saving
				this.saveAndFetchNext()

			} else {                                             // All jobs have a valid customer, save and finish the import

				this.saveJobs(this.allJobs).then(response => {
					vm.setStage('finish-import')
				})
			}
		},

		saveJobs(jobs) {
			let vm = this

			return new Promise((resolve,reject) => {

				jobs = this.cloneObj(jobs).map(job => {
					delete job.fileRowIndex
					return job
				})

				// If we are saving a PROJECT, use /save endpoint
				if (!this.isService) {

					const promises = jobs.map(job => {
						return this.postData('Job/save', job)
					})

					Promise.all(promises).then(responses => {

						responses.forEach((response, index) => {
							if (!response.success) {
								const job = jobs[index]
								const errors = response.errors.join('. ')
								vm.brokenJobs.push([errors, ...vm.fileRows[job.fileRowIndex]])
							} else if ('customer_id' in jobs[index] && jobs[index].customer_id > 0) {
								vm.numImported += 1
							}
						})

						resolve(responses)
					})
				}

				// If we are saving a SERVICE, use /save-batch endpoint
				else {

					const payload = {records: JSON.stringify(jobs)}

					this.postData("Service/save-batch", payload).then(response => {
						if ('form_errors' in response.data && response.data.form_errors) {
							response.data.form_errors.forEach(formError => {
								const job = formError.values
								const errors = Object.values(formError.errors).join('. ')
								vm.brokenJobs.push([errors, ...vm.fileRows[job.fileRowIndex]])
							})
						}

						vm.numImported += response.data.records_saved
						resolve(response)
					})
				}
			})

		},

		fetchAllContacts(setJobs) {
			/* If setJobs is true, also map the jobs to contacts afterwards */
			let vm = this

			this.fetchingContacts = true
			const batchSize = 50
			this.allContacts = []

			let payload = {
				fields: ['email', 'name'],
				custom_page_limit: batchSize,
				page: 1
			}

			// Fetch the first page
			this.getJSON("Contact/get",payload).then(response => {
				if (response.success) {

					if (!response.data.length) {
						vm.alertFail("No company contacts found! Please import your contacts first.")
						vm.fetchingContacts = false
						return
					}

					const pageCount = response.meta.num_pages
					vm.allContacts = response.data

					// Fetch remaining pages of contacts
					let promises = []
					for (let i = 2; i <= pageCount; i++) {   // we already have page #1, start on page #2
						payload.page = i
						promises.push(vm.getJSON('Contact/get', payload))
					}

					// (but only if they exist)
					if (promises.length) {
						Promise.all(promises).then(responses => {
							responses.forEach(response => {
								if (response.success)
									vm.allContacts = [...vm.allContacts, ...response.data]
							})
							vm.fetchingContacts = false

							if (setJobs)
								vm.setAndSaveJobs()
						})

					} else {
						vm.fetchingContacts = false

						if (setJobs)
							vm.setAndSaveJobs()
					}
				}
			})
		},

		fileUploaded(fileHeaders, fileRows) {
			this.fileHeaders = fileHeaders
			this.fileRows = fileRows
		},
		resetImport() {
			/* Reset the fields relating to a file import */
			this.stage = 'upload-file'
			this.fileHeaders = null
			this.fileRows = null
		},
		setStage(stage) {
			this.stage = stage
		},
		fetchData() {
			let vm = this

			const promises = [
				this.getJSON('RevenueCode/get'),
				this.getJSON('Category/get')
			]

			Promise.all(promises).then(responses => {
				if (responses[0].success)
					vm.revenueCodes = responses[0].data
				if (responses[1].success)
					vm.jobCategories = responses[1].data
			})
		}
	},
	created() {
		this.fetchData()
		this.fetchJobDrafts()
	}
}
</script>

<style scoped>

</style>